import { Banknote, Clock, SlidersHorizontal } from 'lucide-react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { StatusPill } from '@/components/ui/StatusPill'
import { Tag } from '@/components/ui/Tag'
import type { LegDraft } from '@/features/cases/components/CaseRecordSheets'
import { useTransportRefs } from '@/features/directories/api/useDirectory'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { placeName } from '@/lib/countries'
import { modeFromCode, transportModeLabel } from '@/lib/icons'
import type { LegRoutePoint, LegStatus } from '@/types/domain'

import type { CarrierSummary } from './legCarriers'
import { LegCarriersLine } from './LegCarriersLine'
import { LegPlace } from './LegPlace'
import { overdue, overdueDays } from './overdue'

/* The top of the leg page (S-10, A3): no form — the two ends as the title with
 * the status pill, one muted line of facts, the route as a strip of points, and
 * «Параметры плеча» at the top right, the way into everything that is not a
 * truck. Since S-18a «Оплатить перевозчику» sits left of it — a SECONDARY
 * button, because the page's one blue button is the save bar's primary act —
 * the way into one payment over several of a carrier's trucks, or the leg as a
 * whole.
 *
 * It reads the page's DRAFT, not only the saved leg: a corridor picked in the
 * drawer retitles the page before anything is saved, which is the honest view
 * of what «Создать плечо» is about to write.
 *
 * Nothing here decides where the cargo is. The strip marks the SERVED current
 * point, and the overdue line compares one served timestamp against the clock —
 * the same arithmetic the page always did, in the same quiet hairline.
 */

const STATUS_TONE: Record<LegStatus, 'neutral' | 'brand' | 'attention' | 'positive'> = {
  planned: 'neutral',
  'in-transit': 'brand',
  'waiting-border': 'attention',
  arrived: 'positive',
  completed: 'positive',
}

/** One point of the strip — a stored point or a drafted one. */
export interface StripPoint {
  key: string
  name: string
  country?: string
  /** The cargo has been declared here (a stored arrival). */
  reached?: boolean
}

export function LegSummary({
  draft,
  sequence,
  caseNumber,
  carriers,
  points,
  currentPointId,
  nextPointDueAt,
  storedPoints,
  automated,
  onOpenParams,
  onPayCarrier,
  onMarkNext,
  markPending,
}: {
  draft: LegDraft
  /** The leg's place in the route, absent on `legs/new`. */
  sequence?: number
  /** Beside the title while the leg does not exist yet. */
  caseNumber?: string
  carriers: CarrierSummary
  points: StripPoint[]
  currentPointId?: string
  nextPointDueAt?: string
  /** The stored chain, for the overdue line's names. */
  storedPoints: LegRoutePoint[]
  /** The chain carries normative days — it moves by itself. */
  automated: boolean
  onOpenParams: () => void
  /** «Оплатить перевозчику»; absent — no button — without
   *  `finance.payment.carrier` or before the leg has a money block. */
  onPayCarrier?: () => void
  /** «Отметить прибытие» on the overdue line; absent for a read-only reader. */
  onMarkNext?: () => void
  markPending?: boolean
}) {
  const { t, i18n } = useTranslation()
  const format = useFormat()
  const { data: modes } = useTransportRefs('mode', true)

  const modeRef = modes?.find((mode) => mode.id === draft.transportModeId)
  const conveyance = modeFromCode(modeRef?.code)
  const reference = draft.reference.trim()
  const hasEnds = draft.origin.trim() !== '' || draft.destination.trim() !== ''

  const currentIndex = storedPoints.findIndex((point) => point.id === currentPointId)
  const next = currentIndex >= 0 ? storedPoints[currentIndex + 1] : undefined
  const late = overdue(nextPointDueAt)
  /* A stored ISO code is named where it is spoken (#136 N-17). */
  const named = (value: string) => placeName(value, i18n.language) ?? value

  /* The facts in the order Operations says them on the phone — mode, who
     carries it, its paper, its dates. A clause with nothing to say is dropped,
     never dashed out. */
  const dates =
    draft.etd && draft.eta
      ? `${format.dateShort(draft.etd)} → ${format.dateShort(draft.eta)}`
      : draft.etd
        ? format.dateShort(draft.etd)
        : draft.eta
          ? `→ ${format.dateShort(draft.eta)}`
          : null

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-text-heading text-[24px] leading-tight font-normal tracking-[-0.5px] sm:text-[28px]">
              {hasEnds ? (
                <>
                  <LegPlace name={draft.origin || '…'} country={draft.originCountry || undefined} />
                  <span className="text-text-muted mx-1.5" aria-hidden>
                    →
                  </span>
                  <LegPlace
                    name={draft.destination || '…'}
                    country={draft.destinationCountry || undefined}
                  />
                </>
              ) : (
                t('cases.legNew.title')
              )}
              {caseNumber ? (
                <span className="text-text-muted ml-2 text-[15px] font-light">· {caseNumber}</span>
              ) : null}
            </h1>
            <StatusPill tone={STATUS_TONE[draft.status]}>{t(`legStatus.${draft.status}`)}</StatusPill>
            {automated ? <Tag>{t('cases.legPage.autoTag')}</Tag> : null}
          </div>

          <div className="text-text-muted flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
            {[
              sequence !== undefined
                ? { key: 'sequence', node: <span>{t('cases.legs.legN', { n: sequence })}</span> }
                : null,
              modeRef ? { key: 'mode', node: <span>{transportModeLabel(modeRef, t)}</span> } : null,
              carriers.kind !== 'none'
                ? { key: 'carriers', node: <LegCarriersLine summary={carriers} /> }
                : null,
              reference
                ? {
                    key: 'reference',
                    node: (
                      <span>
                        {conveyance === 'road' ? t('cases.legs.cmr') : t('cases.legs.waybill')} {reference}
                      </span>
                    ),
                  }
                : null,
              dates ? { key: 'dates', node: <span className="asa-num">{dates}</span> } : null,
            ]
              .filter((clause) => clause !== null)
              /* Keyed by WHAT the clause is, never by its index: the mode
                 clause arrives with the catalogue read, and an index key would
                 remount the carriers' disclosure under the reader's press. */
              .map((clause, index, all) => (
                <Fragment key={clause.key}>
                  {clause.node}
                  {index < all.length - 1 ? <span aria-hidden>·</span> : null}
                </Fragment>
              ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {onPayCarrier ? (
            <Button variant="secondary" iconLeft={Banknote} onClick={onPayCarrier}>
              {t('cases.legPage.payCarrier')}
            </Button>
          ) : null}
          <Button variant="secondary" iconLeft={SlidersHorizontal} onClick={onOpenParams}>
            {t('cases.legParams.open')}
          </Button>
        </div>
      </div>

      {points.length >= 2 ? <PointStrip points={points} currentKey={currentPointId} /> : null}

      {/* The overdue line — quiet, because on a corridor with a norm a late
          transition is the ordinary course of things: the position moves by
          itself, and this exists for the one correction only a person makes. */}
      {late && next ? (
        <div className="border-line-hair flex flex-wrap items-center gap-3 border-t pt-3">
          <Clock size={15} aria-hidden className="text-text-muted shrink-0" />
          <p className="text-text-body min-w-0 flex-1 text-[12.5px] leading-snug">
            {t('cases.legPage.overdueBanner', {
              count: overdueDays(nextPointDueAt),
              from: named(storedPoints[currentIndex]?.name ?? draft.origin),
              to: named(next.name),
            })}
          </p>
          {onMarkNext ? (
            <Button variant="secondary" size="sm" disabled={markPending} onClick={onMarkNext}>
              {t('cases.legPage.cargoAtNext', { point: named(next.name) })}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** The route as a strip of discs. Reached points are filled; the served
 *  current one carries a ring and says so in words to a screen reader.
 *
 *  Every column is at least 5.5rem (88px): a 13-point route fits the 1156px card
 *  of a 1440px screen with no scrolling, and a longer one scrolls inside the
 *  strip rather than squeezing a column below what its label needs.
 *
 *  A label WRAPS inside its own column and is never truncated (#136 N-33): on a
 *  13-point route `truncate` cut «Navoi / Bukhara» to «Navoi / Buk…», and a
 *  place with no break opportunity ran into its neighbour. `break-words` lets
 *  «Turkmenabat» break rather than overflow, so no label can reach another
 *  column. The strip itself scrolls sideways when the columns no longer fit —
 *  it is a read-only line of places, not an editable grid, so the narrow-screen
 *  rule against sideways scrolling does not bite; the padding keeps the current
 *  disc's ring from being clipped by the scroll box. */
function PointStrip({ points, currentKey }: { points: StripPoint[]; currentKey?: string }) {
  const { t } = useTranslation()
  return (
    <ol
      aria-label={t('cases.legPage.routeTitle')}
      /* A scrolling region is focusable so a keyboard reader can move it. */
      tabIndex={0}
      className="relative flex items-start overflow-x-auto px-1 py-1"
    >
      {points.map((point, index) => {
        const current = point.key === currentKey
        const last = index === points.length - 1
        return (
          <li
            key={point.key}
            className={cn(
              'flex flex-col gap-1.5',
              last ? 'w-[5.5rem] shrink-0' : 'min-w-[5.5rem] flex-1',
            )}
          >
            <span className="flex w-full items-center" aria-hidden>
              <span
                className={cn(
                  'size-3 shrink-0 rounded-full border-2',
                  point.reached ? 'bg-text-heading border-text-heading' : 'border-text-muted bg-surface',
                  current && 'ring-brand-soft ring-4',
                )}
              />
              {!last ? <span className="bg-line h-px flex-1" /> : null}
            </span>
            <span className="text-text-body w-full pr-1.5 text-[12.5px] leading-snug break-words">
              <LegPlace name={point.name || '…'} country={point.country} />
              {current ? <span className="sr-only"> — {t('cases.legPage.currentPoint')}</span> : null}
            </span>
          </li>
        )
      })}
    </ol>
  )
}
